import React, { useState, useEffect, useCallback } from "react";
import { Table, Input, Select, Button, Row, Col, Collapse, Checkbox, Dropdown, Modal, Tooltip } from "antd";
import { UpOutlined, DownOutlined, SearchOutlined, DoubleLeftOutlined, LeftOutlined, RightOutlined, DoubleRightOutlined, EllipsisOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useUsageStatus } from "../hooks/useUsageStatus";
import CircularProgressBar from "../components/CircularProgressBar";
import user_image from "../images/user_img.jpg";
import api from "../services/api";
import { toast } from "react-toastify";
import { listTests, retakeTest } from "../services/tests";
import magnifying from "../images/icons/Magnifying.svg";
import '../styles/SearchFilter.css';

const { Option } = Select;

interface TestAssignment {
  id: number;
  user_id: number;
  name: string;
  profile_image?: string;
  test: string;
  test_id: number;
  industry: string;
  industry_id: number;
  software?: string;
  software_id?: number;
  percentile: number | null;
  top_skills: string[];
  location?: string;
}

interface Industry {
  id: number;
  name: string;
}

const ViewCandidates: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useUser();

  // Usage status for checking limits
  const { usage, refresh: refreshUsage } = useUsageStatus();

  const [candidates, setCandidates] = useState<TestAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter states
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [filterTestId, setFilterTestId] = useState<number | null>(null);
  const [filterTestName, setFilterTestName] = useState<string | null>(null);

  // Sorting state
  const [sortOption, setSortOption] = useState<'name' | 'percentile' | 'software'>('name');

  // View toggle state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Selected candidates state for both views - persistent across pagination/search
  const [selectedCandidates, setSelectedCandidates] = useState<TestAssignment[]>([]);

  // Helper function to get currently selected IDs for current page
  const getSelectedIdsForCurrentPage = () => {
    return selectedCandidates.map(candidate => candidate.id);
  };

  // Reassess modal states
  const [openReassessModal, setOpenReassessModal] = useState(false);
  const [reassessCandidate, setReassessCandidate] = useState<TestAssignment | null>(null);
  const [availableTests, setAvailableTests] = useState<any[]>([]);
  const [selectedTestId, setSelectedTestId] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  // Data for filters
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [locations, setLocations] = useState<string[]>([]);

  // Pagination (TalentPool style)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [total, setTotal] = useState(0);

  // Sorting function for candidates
  const sortCandidates = (candidatesToSort: TestAssignment[]) => {
    return [...candidatesToSort].sort((a, b) => {
      switch (sortOption) {
        case 'name':
          // Primary: name, Secondary: percentile (highest first)
          const nameComparison = a.name.localeCompare(b.name);
          if (nameComparison !== 0) return nameComparison;
          return (b.percentile || 0) - (a.percentile || 0);
        case 'percentile':
          // Primary: percentile (highest first), Secondary: name
          const percentileA = a.percentile || 0;
          const percentileB = b.percentile || 0;
          const percentileComparison = percentileB - percentileA;
          if (percentileComparison !== 0) return percentileComparison;
          return a.name.localeCompare(b.name);
        case 'software':
          // Primary: software, Secondary: name
          const softwareA = a.software || 'N/A';
          const softwareB = b.software || 'N/A';
          const softwareComparison = softwareA.localeCompare(softwareB);
          if (softwareComparison !== 0) return softwareComparison;
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
  };

  // Helper functions for usage validation
  const isUsageLimitReached = () => {
    // Super admins never hit usage limits
    if (user?.is_super_admin) return false;

    return usage?.exceeded ||
      (!usage?.has_subscription && usage?.limit === 0) ||
      (usage?.remaining !== undefined && usage.remaining <= 0);
  };

  const isUsageAtRisk = () => {
    // Super admins never at risk
    if (user?.is_super_admin) return false;

    return usage?.at_risk && !isUsageLimitReached();
  };

  const handleUsageValidation = async (action: 'send' | 'reassess', callback: () => void) => {
    if (usage?.exceeded) {
      Modal.confirm({
        title: 'Subscription Limit Reached',
        content: `You have reached your assessment invitation limit (${usage.current_usage}/${usage.limit}). Please upgrade your plan to send more invitations.`,
        okText: 'Upgrade Plan',
        cancelText: 'Cancel',
        onOk: () => navigate('/subscriptions'),
      });
      return;
    }

    if (usage?.at_risk) {
      Modal.confirm({
        title: 'Approaching Usage Limit',
        content: `You are approaching your invitation limit (${usage.current_usage}/${usage.limit}). Do you want to continue?`,
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: callback,
      });
      return;
    }

    // If no usage issues, proceed directly
    callback();
  };

  const loadIndustries = async () => {
    try {
      const response = await api.get('industries/');
      setIndustries(response.data);
    } catch (error) {
      console.error('Failed to load industries:', error);
      toast.error('Failed to load industries');
    }
  };

  const loadLocations = async () => {
    try {
      // Get all candidates without pagination to extract unique locations
      const response = await api.get('my-applicants/?page_size=1000');
      const allCandidates = response.data.results;

      // Extract unique non-empty locations
      const uniqueLocations = Array.from(new Set(
        allCandidates
          .map((candidate: TestAssignment) => candidate.location)
          .filter((location: string | undefined): location is string =>
            location !== undefined && location !== null && location.trim() !== ''
          )
      )).sort() as string[];

      setLocations(uniqueLocations);
    } catch (error) {
      console.error('Failed to load locations:', error);
      toast.error('Failed to load locations');
    }
  };

  const loadCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (selectedIndustryIds.length > 0) {
        selectedIndustryIds.forEach(id => params.append('industry', id.toString()));
      }
      if (selectedLocationIds.length > 0) {
        selectedLocationIds.forEach(location => params.append('location', location));
      }
      if (filterTestId) {
        params.append('test', filterTestId.toString());
        console.log('Filtering by assessment:', filterTestId);
      }
      if (searchTerm.trim()) params.append('search', searchTerm); // This will search by user name
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      console.log('API call parameters:', params.toString());
      const response = await api.get(`my-applicants/?${params.toString()}`);

      // The backend already provides top_skills, so we don't need to fetch them separately
      setCandidates(response.data.results);
      console.log('Assessment assignments:', response.data.results);
      console.log('Filter assessment ID being used:', filterTestId);

      // If filtering by assessment and we have results, extract the test name from the first result
      if (filterTestId && response.data.results.length > 0) {
        setFilterTestName(response.data.results[0].test);
      } else if (!filterTestId) {
        setFilterTestName(null);
      }

      setTotal(response.data.count);
    } catch (error) {
      console.error('Failed to load candidates:', error);
      toast.error('Failed to load candidates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [selectedIndustryIds, selectedLocationIds, searchTerm, page, pageSize, filterTestId]);

  // Check if user is admin, if not redirect
  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    loadIndustries();
    loadLocations();

    // Initialize filters from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const testIdParam = urlParams.get('test_id');
    const testNameParam = urlParams.get('test_name');
    console.log('URL test_id parameter:', testIdParam);
    console.log('URL test_name parameter:', testNameParam);
    if (testIdParam) {
      const testId = parseInt(testIdParam, 10);
      console.log('Setting filterTestId to:', testId);
      setFilterTestId(testId);
    }
    if (testNameParam) {
      console.log('Setting filterTestName to:', testNameParam);
      setFilterTestName(decodeURIComponent(testNameParam));
    }
  }, [location.search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCandidates();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [loadCandidates]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleClearTestFilter = () => {
    setFilterTestId(null);
    setFilterTestName(null);
    setPage(1);
    // Clear URL parameters
    navigate('/view-candidates', { replace: true });
  };

  // Helper function to render empty state based on filter status
  const renderEmptyState = () => {
    if (filterTestId && filterTestName) {
      // When assessment filter is applied but no candidates found
      return (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          color: '#666',
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#1a1a1a',
            marginBottom: '8px',
          }}>
            No applicants found for this assessment
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '20px',
          }}>
            No candidates have been invited to take the "{filterTestName}" assessment yet.
          </div>
          <Button
            type="primary"
            style={{
              background: '#2563eb',
              borderColor: '#2563eb',
              borderRadius: '6px',
              fontWeight: 500,
            }}
            onClick={() => navigate('/candidate/register')}
          >
            Invite candidates for this assessment
          </Button>
        </div>
      );
    } else {
      // When no filter is applied - general empty state
      return (
        <div style={{
          padding: '60px 20px',
          textAlign: 'center',
          color: '#666',
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#1a1a1a',
            marginBottom: '8px',
          }}>
            No assessment results found
          </div>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginBottom: '20px',
          }}>
            Start building your assessment results by inviting qualified candidates to showcase their skills
          </div>
          <Button
            type="primary"
            style={{
              background: '#2563eb',
              borderColor: '#2563eb',
              borderRadius: '6px',
              fontWeight: 500,
            }}
            onClick={() => navigate('/candidate/register')}
          >
            Invite your first candidate
          </Button>
        </div>
      );
    }
  };

  const handleCardSelection = (assignmentId: number) => {
    const candidateToToggle = candidates.find(c => c.id === assignmentId);
    if (!candidateToToggle) return;

    setSelectedCandidates(prev => {
      const isAlreadySelected = prev.some(selected => selected.id === assignmentId);

      if (isAlreadySelected) {
        // Remove from selection
        return prev.filter(selected => selected.id !== assignmentId);
      } else {
        // Add to selection
        return [...prev, candidateToToggle];
      }
    });
  };

  const handleCompareClick = () => {
    if (selectedCandidates.length === 0) {
      toast.warning('Please select at least one candidate to compare');
      return;
    }

    // Transform the data to match CandidateComparison interface
    const candidatesForComparison = selectedCandidates.map(assignment => ({
      candidate_id: assignment.user_id,
      name: assignment.name,
      image_url: assignment.profile_image,
      email: '', // We don't have email in current interface, API will provide it
      skills: {}, // API will provide skills data
      score: assignment.percentile || 0,
      training_cost: 0, // API will calculate this
      test_name: assignment.test,
      test_id: assignment.test_id,
      test_assignment_id: assignment.id,
      industry_id: assignment.industry_id,
    }));

    // Navigate to compare page with selected candidates
    navigate('/candidate/compare', {
      state: {
        preSelectedCandidates: candidatesForComparison
      }
    });
  };

  const clearAllFilters = () => {
    setSelectedIndustryIds([]);
    setSelectedLocationIds([]);
    setSearchTerm("");
    setPage(1);
  };

  const handleTableChange = (paginationConfig: any) => {
    setPage(paginationConfig.current);
    setPageSize(paginationConfig.pageSize);
  };

  // Action handlers for ellipsis menu
  const handleSeeTestResults = (assignment: TestAssignment) => {
    if (assignment.id) {
      navigate(`/candidate/test-result?assignment_id=${assignment.id}`);
    } else {
      toast.error('Assessment ID not found for this candidate.');
    }
  };

  const handleTrackProgress = (assignment: TestAssignment) => {
    if (assignment.user_id) {
      navigate(`/candidate/track-progress/${assignment.user_id}`);
    } else {
      toast.error('User ID not found for this candidate.');
    }
  };

  const handleViewApplicantProfile = (assignment: TestAssignment) => {
    if (assignment.user_id) {
      navigate(`/candidate/profile/${assignment.user_id}`);
    } else {
      toast.error('User ID not found for this candidate.');
    }
  };

  const handleReassessApplicant = async (assignment: TestAssignment) => {
    // First validate usage before proceeding
    handleUsageValidation('reassess', async () => {
      setReassessCandidate(assignment);
      setSelectedTestId(null);

      // Load available tests
      try {
        const testsResponse = await listTests();
        setAvailableTests(Array.isArray(testsResponse) ? testsResponse : []);
        setOpenReassessModal(true);
      } catch (error) {
        console.error('Failed to load tests:', error);
        toast.error('Failed to load available assessments');
      }
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: TestAssignment) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src={record.profile_image || user_image}
              alt={record.name}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
            <span>{record.name}</span>
          </div>
        );
      },
    },
    {
      title: "Assessment",
      dataIndex: "test",
      key: "test",
      render: (test: string) => (
        <span>
          {test || 'N/A'}
        </span>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: string) => (
        <span>{location || 'Not specified'}</span>
      ),
    },
    {
      title: "Score",
      dataIndex: "percentile",
      key: "percentile",
      width: 70,
      render: (percentile: number | null) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {percentile !== null ? (
            <CircularProgressBar
              percentage={percentile}
              size={50}
              strokeWidth={4}
              progressColor="#0051FF"
            />
          ) : (
            <span>Pending</span>
          )}
        </div>
      ),
    },
    {
      title: "Top skills",
      dataIndex: "top_skills",
      key: "top_skills",
      render: (skills: string[]) => (
        <span>{skills.join(', ')}</span>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '5%',
      render: (_: any, assignment: TestAssignment) => {
        const menuItems = [
          {
            key: 'see-test-results',
            label: (
              <span onClick={() => handleSeeTestResults(assignment)}>
                See assessment results
              </span>
            ),
          },
          {
            key: 'track-progress',
            label: (
              <span onClick={() => handleTrackProgress(assignment)}>
                Track progress
              </span>
            ),
          },
          {
            key: 'view-applicant-profile',
            label: (
              <span onClick={() => handleViewApplicantProfile(assignment)}>
                View applicant profile
              </span>
            ),
          },
          {
            key: 'reassess-applicant',
            label: (
              <span onClick={() => handleReassessApplicant(assignment)}>
                Reassess applicant
              </span>
            ),
          },
        ];
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span
              className="applicants-ellipsis"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <EllipsisOutlined style={{ fontSize: 16, color: '#bfc5d2' }} />
            </span>
          </Dropdown>
        );
      },
    },
  ];

  // Don't render if not admin
  if (!isAdmin()) {
    return null;
  }

  return (
    <div style={containerStyle}>
      <style>{`
        .custom-sort-dropdown .ant-select-item {
          font-size: 12px !important;
        }
        .custom-sort-dropdown .ant-select-selection-item {
          font-size: 12px !important;
        }
        .ant-select-selector {
          font-size: 12px !important;
        }
      `}</style>
      <Row className="search-filter" gutter={[8, 0]} style={{ height: '100vh', margin: 0, columnGap: "30px" }}>
        {/* Filters Sidebar */}
        <Col xs={24} lg={6} style={{ height: '100vh', paddingLeft: 0, maxWidth: '260px', flex: '0 0 260px' }}>
          <div style={filtersContainerStyle}>
            <div style={filtersHeaderStyle}>
              <h3 style={filtersTitleStyle}>Filters</h3>
              <Button type="link" onClick={clearAllFilters} style={clearAllStyle}>
                Clear all
              </Button>
            </div>

            {/* Collapsible Filters */}
            <Collapse
              defaultActiveKey={['industries']}
              ghost
              expandIconPosition="end"
              expandIcon={({ isActive }) => isActive ? <UpOutlined /> : <DownOutlined />}
              style={{ background: 'transparent' }}
            >
              {/* Industries Filter */}
              <Collapse.Panel header="Industries" key="industries" style={panelStyle}>
                <div style={checkboxGroupStyle}>
                  {industries.map(industry => (
                    <div key={industry.id} style={checkboxItemStyle}>
                      <Checkbox
                        checked={selectedIndustryIds.includes(industry.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIndustryIds(prev => [...prev, industry.id]);
                          } else {
                            setSelectedIndustryIds(prev => prev.filter(id => id !== industry.id));
                          }
                          setPage(1);
                        }}
                        style={checkboxStyle}
                      >
                        {industry.name}
                      </Checkbox>
                    </div>
                  ))}
                </div>
                <Button type="link" onClick={() => {
                  setSelectedIndustryIds([]);
                  setSelectedLocationIds([]);
                  setFilterTestId(null);
                  setFilterTestName(null);
                  setPage(1);
                  // Clear URL parameters
                  navigate('/view-candidates', { replace: true });
                }} style={clearFiltersStyle}>
                  Clear filters
                </Button>
              </Collapse.Panel>

              {/* Location Filter */}
              <Collapse.Panel header="Location" key="location" style={panelStyle}>
                <div style={checkboxGroupStyle}>
                  {locations.length > 0 ? (
                    locations.map(location => (
                      <div key={location} style={checkboxItemStyle}>
                        <Checkbox
                          checked={selectedLocationIds.includes(location)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLocationIds(prev => [...prev, location]);
                            } else {
                              setSelectedLocationIds(prev => prev.filter(loc => loc !== location));
                            }
                            setPage(1);
                          }}
                          style={checkboxStyle}
                        >
                          {location}
                        </Checkbox>
                      </div>
                    ))
                  ) : (
                    <div style={noDataMessageStyle}>
                      No locations available
                    </div>
                  )}
                </div>
                {locations.length > 0 && (
                  <Button type="link" onClick={() => {
                    setSelectedLocationIds([]);
                    setPage(1);
                  }} style={clearFiltersStyle}>
                    Clear filters
                  </Button>
                )}
              </Collapse.Panel>
            </Collapse>
          </div>
        </Col>

        {/* Main Content */}
        <Col xs={24} lg={20} style={{ height: '100vh', paddingRight: 0, flex: '1', minWidth: 0 }}>
          <div style={mainContentStyle}>
            {/* Header Row 1: Title and Compare Button */}
            <div style={headerRowStyle}>
              <h2 style={titleStyle}>My Applicants</h2>
              <Button
                type="default"
                shape="round"
                style={{
                  border: 'none',
                  fontWeight: 500,
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.2)',
                  borderRadius: '24px',
                  padding: '0 18px',
                  height: '32px !important',
                  minWidth: '200px',
                  background: "#1d1f48"
                }}
                onClick={handleCompareClick}
                disabled={selectedCandidates.length === 0}
              >
                Compare candidates
                {selectedCandidates.length > 0 && (
                  <span style={{
                    marginLeft: '8px',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    {selectedCandidates.length}
                  </span>
                )}
              </Button>
            </div>

            {/* Header Row 2: Search and Controls */}
            <div style={controlsRowStyle} className="input-form">
              <div style={{ flex: 1, marginRight: 16 }}>
                <Input
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{
                    borderRadius: 24,
                    width: "100%",
                    background: "#eaf2ff",
                    border: "none",
                    color: "#23244A",
                    fontWeight: 500,
                    height: 32,
                    fontSize: 14,
                    paddingLeft: 24,
                    paddingRight: 24,
                  }}
                  suffix={
                    searchTerm ? (
                      <span
                        style={{ cursor: 'pointer', color: '#787A7D', marginRight: 4 }}
                        onClick={() => handleSearchChange("")}
                      >
                        ×
                      </span>
                    ) : (
                      <img src={magnifying} style={{ height: 15, width: 15 }} />
                    )
                  }
                />
              </div>

              {/* Show selection info for both views */}
              {selectedCandidates.length > 0 && (
                <div style={{ position: 'relative' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginRight: '16px',
                      padding: '6px 12px',
                      background: '#e6f3ff',
                      borderRadius: '20px',
                      fontSize: '14px',
                      color: '#0066cc',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      if (tooltip) tooltip.style.display = 'block';
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      setTimeout(() => {
                        if (tooltip && !tooltip.matches(':hover')) {
                          tooltip.style.display = 'none';
                        }
                      }, 100);
                    }}
                  >
                    <span>{selectedCandidates.length} selected</span>
                    <button
                      onClick={() => {
                        setSelectedCandidates([]);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0066cc',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: '0',
                        lineHeight: '1',
                      }}
                      title="Clear all selections"
                    >
                      ×
                    </button>
                  </div>

                  {/* Tooltip with candidate details */}
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      marginTop: '8px',
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                      padding: '12px',
                      minWidth: '300px',
                      maxWidth: '400px',
                      zIndex: 1000,
                      display: 'none',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.display = 'block';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  >
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: '8px',
                      borderBottom: '1px solid #e5e7eb',
                      paddingBottom: '8px'
                    }}>
                      Selected Candidates ({selectedCandidates.length})
                    </div>
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {selectedCandidates.map((candidate) => (
                        <div
                          key={candidate.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 0',
                            borderBottom: '1px solid #f3f4f6',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#1f2937',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {candidate.name}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              marginTop: '2px'
                            }}>
                              {candidate.test} • {candidate.percentile !== null ? `${candidate.percentile}%` : 'Pending'}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedCandidates(prev =>
                                prev.filter(selected => selected.id !== candidate.id)
                              );
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '14px',
                              padding: '4px',
                              borderRadius: '4px',
                              marginLeft: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '20px',
                              height: '20px',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fef2f2';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'none';
                            }}
                            title={`Remove ${candidate.name} from selection`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div style={rightControlsStyle}>
                <Button
                  type="default"
                  style={inviteCandidateButtonStyle}
                  onClick={() => navigate('/candidate/register')}
                >
                  Invite candidate
                </Button>
                <div style={sortContainerStyle}>
                  <Select
                    value={sortOption}
                    onChange={(value) => setSortOption(value)}
                    style={{
                      width: 180,
                      borderRadius: 24,
                      background: "#eaf2ff",
                      border: "none",
                      color: "#23244A",
                      fontWeight: 500,
                      height: 32,
                      fontSize: '12px !important',
                    }}
                    size="large"
                    bordered={false}
                    dropdownStyle={{
                      borderRadius: 8,
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                      minWidth: 180,
                      fontSize: '12px !important',
                    }}
                    dropdownClassName="custom-sort-dropdown"
                  >
                    <Option value="name">Sort by Name (A-Z)</Option>
                    <Option value="percentile">Sort by Score (High-Low)</Option>
                    <Option value="software">Sort by Software (A-Z)</Option>
                  </Select>
                </div>
                <div style={viewToggleStyle}>
                  {/* List View Icon */}
                  <Button
                    type={viewMode === 'list' ? 'primary' : 'default'}
                    size="small"
                    className="toggle-button"
                    style={{
                      ...toggleButtonStyle,
                      backgroundColor: viewMode === 'list' ? '#2563eb' : 'transparent',
                      borderColor: 'transparent',
                    }}
                    onClick={() => setViewMode('list')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <rect x="2" y="3" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                      <rect x="2" y="7.25" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                      <rect x="2" y="11.5" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                    </svg>
                  </Button>

                  {/* Grid View Icon */}
                  <Button
                    type={viewMode === 'grid' ? 'primary' : 'default'}
                    size="small"
                    className="toggle-button"
                    style={{
                      ...toggleButtonStyle,
                      backgroundColor: viewMode === 'grid' ? '#2563eb' : 'transparent',
                      borderColor: 'transparent',
                    }}
                    onClick={() => setViewMode('grid')}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <rect x="2" y="2" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                      <rect x="9" y="2" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                      <rect x="2" y="9" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                      <rect x="9" y="9" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                    </svg>
                  </Button>
                </div>
              </div>

              {/* Reassess Modal */}
              <Modal
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ background: '#D6F3FF', borderRadius: 10, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L13.09 8.26L19 7L14.5 11.5L16 18L12 16L8 18L9.5 11.5L5 7L10.91 8.26L12 2Z" fill="#0051FF" />
                      </svg>
                    </div>
                    <div>
                      <h4 style={{ fontWeight: 700, fontSize: 24, color: '#1D1F48', marginBottom: 0 }}>Reassess {reassessCandidate?.name}</h4>
                      <p style={{ fontSize: 14, color: '#6B7280', margin: 0 }}>Select an assessment to send invitation</p>
                    </div>
                  </div>
                }
                open={openReassessModal}
                onCancel={() => {
                  setOpenReassessModal(false);
                  setReassessCandidate(null);
                  setSelectedTestId(null);
                  setSending(false);
                }}
                footer={[
                  <Button
                    key="cancel"
                    onClick={() => {
                      setOpenReassessModal(false);
                      setReassessCandidate(null);
                      setSelectedTestId(null);
                      setSending(false);
                    }}
                    disabled={sending}
                  >
                    Cancel
                  </Button>,
                  <Button
                    key="send"
                    type="primary"
                    disabled={!selectedTestId || sending}
                    onClick={async () => {
                      if (!selectedTestId || !reassessCandidate) return;

                      setSending(true);
                      try {
                        await retakeTest({ user_id: reassessCandidate.user_id, test_id: selectedTestId });
                        toast.success('Reassess invitation sent successfully!');
                        setOpenReassessModal(false);
                        setReassessCandidate(null);
                        setSelectedTestId(null);
                        // Refresh usage status after successful send
                        refreshUsage();
                        // Refresh the candidates list
                        loadCandidates();
                      } catch (error) {
                        console.error('Failed to send reassess invitation:', error);
                        toast.error('Failed to send reassess invitation');
                      }
                      setSending(false);
                    }}
                  >
                    {sending ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 8, width: 18, height: 18, borderWidth: 2 }}></span>
                        Sending...
                      </span>
                    ) : (
                      'Send'
                    )}
                  </Button>
                ]}
                width={600}
                centered
              >
                <div style={{ padding: '20px 0' }}>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151' }}>
                      Select Assessment
                    </label>
                    <Select
                      placeholder="Choose an assessment to assign"
                      style={{ width: '100%', height: 48 }}
                      value={selectedTestId}
                      onChange={(value) => setSelectedTestId(value)}
                      disabled={sending}
                    >
                      {availableTests.map((test) => (
                        <Option key={test.id} value={test.id}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 500 }}>{test.name}</span>
                            <span style={{ color: '#6B7280', fontSize: 12 }}>
                              {test.software_name || 'General'} • {test.question_count || 0} questions
                            </span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Usage Warning */}
                  {isUsageAtRisk() && (
                    <div style={{
                      background: '#FEF3C7',
                      border: '1px solid #F59E0B',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 1L15 15H1L8 1Z" fill="#F59E0B" />
                        <path d="M8 6V10" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="8" cy="12.5" r="0.75" fill="white" />
                      </svg>
                      <span style={{ fontSize: 14, color: '#92400E' }}>
                        You are approaching your invitation limit ({usage?.current_usage}/{usage?.limit}).
                      </span>
                    </div>
                  )}

                  {reassessCandidate && (
                    <div style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: 8,
                      padding: 16,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}>
                      <img
                        src={reassessCandidate.profile_image || user_image}
                        alt={reassessCandidate.name}
                        style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.src = user_image;
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, color: '#374151' }}>{reassessCandidate.name}</div>
                        <div style={{ color: '#6B7280', fontSize: 14 }}>
                          Previous assessment: {reassessCandidate.test} • {reassessCandidate.industry}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Modal>
            </div>

            {/* Test Filter Warning */}
            {filterTestId && filterTestName && (
              <div style={{
                background: '#FFF3CD',
                border: '1px solid #FFEB99',
                borderRadius: '8px',
                padding: '12px 16px',
                margin: '6px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    background: '#F59E0B',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginRight: '8px'
                  }}>
                    !
                  </span>
                  <span style={{ color: '#92400E', fontSize: '14px', fontWeight: '500' }}>
                    Showing applicants only for: <strong>{filterTestName}</strong>
                  </span>
                </div>
                <Button
                  type="text"
                  size="small"
                  onClick={handleClearTestFilter}
                  style={{
                    color: '#92400E',
                    fontWeight: '600',
                    padding: '4px 12px',
                    height: 'auto',
                    border: '1px solid #F59E0B',
                    borderRadius: '6px',
                    background: 'rgba(245, 158, 11, 0.1)'
                  }}
                >
                  Show all applicants
                </Button>
              </div>
            )}

            {/* Candidates Table/Grid */}
            <div style={tableContainerStyle}>
              {viewMode === 'list' ? (
                <Table
                  columns={columns}
                  dataSource={sortCandidates(candidates)}
                  loading={loading}
                  pagination={false}
                  onChange={handleTableChange}
                  rowKey="id"
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: getSelectedIdsForCurrentPage(),
                    onChange: (selectedRowKeys, selectedRows) => {
                      // Get current page selected and deselected items
                      const currentPageIds = candidates.map(c => c.id);

                      // Remove previously selected items from current page
                      const withoutCurrentPageSelections = selectedCandidates.filter(selected =>
                        !currentPageIds.includes(selected.id)
                      );

                      // Add new selections from current page
                      const newSelections = [...withoutCurrentPageSelections, ...selectedRows];
                      setSelectedCandidates(newSelections);
                    },
                  }}
                  style={tableStyle}
                  scroll={{ y: 'calc(100vh - 280px)' }}
                  onRow={(record) => ({
                    onClick: (event) => {
                      // Don't navigate if clicking on checkbox or ellipsis menu
                      const target = event.target as HTMLElement;
                      const isCheckbox = target.closest('.ant-checkbox-wrapper') || target.closest('.ant-checkbox');
                      const isEllipsis = target.closest('.applicants-ellipsis') || target.closest('.ant-dropdown');

                      if (!isCheckbox && !isEllipsis && record.user_id) {
                        window.location.href = `/candidate/profile/${record.user_id}`;
                      }
                    },
                    style: { cursor: 'pointer' },
                  })}
                  locale={{
                    emptyText: renderEmptyState()
                  }}
                />
              ) : (
                // Grid View
                <div style={{
                  background: "transparent",
                  borderRadius: 8,
                  height: 'calc(100vh - 280px)',
                  overflowY: 'auto',
                }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                      <div>Loading candidates...</div>
                    </div>
                  ) : candidates.length === 0 ? (
                    renderEmptyState()
                  ) : (
                    <div style={{
                      display: 'grid',
                      // minmax(0, 1fr) — NOT minmax(auto, 1fr) — otherwise
                      // grid columns grow to fit the nowrap skills row's
                      // min-content (chip max-widths + +N chip) and cards
                      // widen past 3-per-row.
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: '16px',
                      padding: '8px',
                    }}>
                      {sortCandidates(candidates).map((assignment) => (
                        <div
                          key={assignment.id}
                          style={{
                            position: 'relative',
                            background: '#fff',
                            border: selectedCandidates.some(s => s.id === assignment.id) ? '2px solid #2563eb' : '1px solid #e8eef7',
                            borderRadius: '18px',
                            height: '140px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: selectedCandidates.some(s => s.id === assignment.id) ? '0 4px 12px rgba(37, 99, 235, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '16px',
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedCandidates.some(s => s.id === assignment.id)) {
                              e.currentTarget.style.borderColor = '#2563eb';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!selectedCandidates.some(s => s.id === assignment.id)) {
                              e.currentTarget.style.borderColor = '#e8eef7';
                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                            }
                          }}
                          onClick={(e) => {
                            // Don't navigate if clicking the checkbox
                            const target = e.target as HTMLElement;
                            const isCheckbox = target.closest('.candidate-checkbox') || target.closest('svg') || target.closest('path');

                            if (!isCheckbox && assignment.user_id) {
                              window.location.href = `/candidate/profile/${assignment.user_id}`;
                            }
                          }}
                        >
                          {/* Selection Checkbox */}
                          <div
                            className="candidate-checkbox"
                            style={{
                              position: 'absolute',
                              top: '12px',
                              right: '12px',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              border: selectedCandidates.some(s => s.id === assignment.id) ? '2px solid #2563eb' : '2px solid #d1d5db',
                              background: selectedCandidates.some(s => s.id === assignment.id) ? '#2563eb' : '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardSelection(assignment.id);
                            }}
                          >
                            <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                              <path
                                d="M1 4.5l3 3L11 1"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                opacity={selectedCandidates.some(s => s.id === assignment.id) ? "1" : "0"}
                              />
                            </svg>
                          </div>

                          {/* Candidate Photo */}
                          <div style={{
                            marginRight: '16px',
                            flexShrink: 0,
                          }}>
                            <img
                              src={assignment.profile_image || user_image}
                              alt={assignment.name}
                              style={{
                                width: '68px',
                                height: '68px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid #f3f4f6',
                              }}
                            />
                          </div>

                          {/* Content Section */}
                          <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minWidth: 0, // Allow shrinking
                          }}>
                            {/* Candidate Name */}
                            <h3 style={{
                              fontSize: '16px',
                              fontWeight: 600,
                              color: '#1f2937',
                              margin: '0 0 4px 0',
                              lineHeight: '1.2',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {assignment.name}
                            </h3>

                            {/* Test Name — single line ellipsis + tooltip. When
                                skills row is empty or has only 1 chip we let
                                the name wrap to a second line (clamped) so
                                long test names stay readable. */}
                            <div style={{
                              marginBottom: '8px',
                              minWidth: 0,
                            }}>
                              <Tooltip title={assignment.test || 'N/A'} placement="top">
                                <span style={{
                                  display: (assignment.top_skills?.length ?? 0) <= 1 ? '-webkit-box' : 'block',
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: (assignment.top_skills?.length ?? 0) <= 1 ? 'normal' : 'nowrap',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: '1.3',
                                  maxWidth: '100%',
                                } as React.CSSProperties}>
                                  {assignment.test || 'N/A'}
                                </span>
                              </Tooltip>
                            </div>

                            {/* Top Skills — show first 3 with per-chip ellipsis
                                + tooltip. Overflow chip "+N" lists the rest in
                                its tooltip. Wraps to 2 lines max (chips fit as
                                many per line as their width allows; card
                                height 140px accommodates two rows). */}
                            {assignment.top_skills && assignment.top_skills.length > 0 && (
                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                minWidth: 0,
                                maxHeight: '2.6rem',
                                overflow: 'hidden',
                              }}>
                                {assignment.top_skills.slice(0, 3).map((skill, index) => (
                                  <Tooltip key={index} title={skill} placement="top">
                                    <span
                                      style={{
                                        background: '#F4F8FF',
                                        color: '#8E99BA',
                                        padding: '0px 12px',
                                        borderRadius: '999px',
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        lineHeight: "1.2rem",
                                        maxWidth: '180px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {skill}
                                    </span>
                                  </Tooltip>
                                ))}
                                {assignment.top_skills.length > 3 && (
                                  <Tooltip
                                    title={assignment.top_skills.slice(3).join(', ')}
                                    placement="top"
                                  >
                                    <span
                                      style={{
                                        background: '#EEF2FF',
                                        color: '#4F46E5',
                                        padding: '0px 10px',
                                        borderRadius: '999px',
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        lineHeight: "1.2rem",
                                        cursor: 'default',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      +{assignment.top_skills.length - 3}
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Pagination Section */}
            <div style={paginationContainerStyle}>
              {/* Left side: Items per page and item range */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
              }}>
                <span style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#23275C',
                }}>Items per page:</span>
                <select
                  style={{
                    background: '#E5EBFA',
                    border: 'none',
                    borderRadius: '24px',
                    padding: '8px 16px ',
                    fontWeight: 600,
                    fontSize: '14px',
                    color: '#23275C',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                >
                  {[15, 20, 50].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#23275C',
                }}>{total === 0 ? '0 - 0 of 0 items' : `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} of ${total} items`}</span>
              </div>
              {/* Right side: Pagination arrows and page numbers */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <button
                  style={{
                    background: '#F9FBFE',
                    border: '1px solid #E6EBFA',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#97A8BC',
                    fontWeight: 400,
                    fontSize: '18px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.5 : 1,
                    boxShadow: '0 2px 8px 0 rgba(35, 39, 92, 0.04)',
                  }}
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                ><DoubleLeftOutlined /></button>
                <button
                  style={{
                    background: '#F9FBFE',
                    border: '1px solid #E6EBFA',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#97A8BC',
                    fontWeight: 400,
                    fontSize: '18px',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.5 : 1,
                    boxShadow: '0 2px 8px 0 rgba(35, 39, 92, 0.04)',
                  }}
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                ><LeftOutlined /></button>
                {/* Page Numbers */}
                {Array.from({ length: Math.min(10, Math.ceil(total / pageSize)) }, (_, i) => (
                  <button key={i + 1} onClick={() => setPage(i + 1)} style={{
                    background: page === i + 1 ? '#eaf2ff' : 'transparent',
                    color: page === i + 1 ? '#23244A' : '#A0AEC0',
                    border: 'none',
                    borderRadius: '50%',
                    width: 35,
                    height: 35,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: page === i + 1 ? 700 : 500,
                    fontSize: 16,
                    boxShadow: 'none',
                    margin: '0 2px',
                    transition: 'background 0.2s',
                  }}>{i + 1}</button>
                ))}
                {Math.ceil(total / pageSize) > 10 && <span style={{ color: '#A0AEC0', fontWeight: 500, margin: '0 8px', fontSize: 20 }}>...</span>}
                {Math.ceil(total / pageSize) > 10 && (
                  <button key={Math.ceil(total / pageSize)} onClick={() => setPage(Math.ceil(total / pageSize))} style={{
                    background: page === Math.ceil(total / pageSize) ? '#eaf2ff' : 'transparent',
                    color: page === Math.ceil(total / pageSize) ? '#23244A' : '#A0AEC0',
                    border: 'none',
                    borderRadius: '50%',
                    width: 35,
                    height: 35,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: page === Math.ceil(total / pageSize) ? 700 : 500,
                    fontSize: 20,
                    boxShadow: 'none',
                    margin: '0 2px',
                    transition: 'background 0.2s',
                  }}>{Math.ceil(total / pageSize)}</button>
                )}
                <button disabled={page === Math.ceil(total / pageSize) || total === 0} onClick={() => setPage(page + 1)} style={{ background: '#F9FBFE', color: '#97A8BC', border: '1px solid #E6EBFA', borderRadius: '50%', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: (page === Math.ceil(total / pageSize) || total === 0) ? 0.4 : 1, transition: 'background 0.2s' }}><RightOutlined /></button>
                <button disabled={page === Math.ceil(total / pageSize) || total === 0} onClick={() => setPage(Math.ceil(total / pageSize))} style={{ background: '#F9FBFE', color: '#97A8BC', border: '1px solid #E6EBFA', borderRadius: '50%', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: (page === Math.ceil(total / pageSize) || total === 0) ? 0.4 : 1, transition: 'background 0.2s' }}><DoubleRightOutlined /></button>
              </div>
            </div>

          </div>
        </Col>
      </Row>
    </div>
  );
};

// Styles
const containerStyle: React.CSSProperties = {
  height: "100vh",
  display: 'flex',
  flexDirection: 'column',
};

const filtersContainerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 8,
  padding: 20,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  overflowY: 'auto',
  maxWidth: '260px',
  width: '100%',
};

const mainContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
};

const tableContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '100%',
  flex: 1,
};

const paginationContainerStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '16px',
  marginBottom: '20px',
  maxWidth: '100%'
};

const filtersHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
};

const filtersTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1a1a1a',
  margin: 0,
};

const clearAllStyle: React.CSSProperties = {
  color: '#1677ff',
  padding: 0,
  fontSize: 14,
  fontWeight: 500,
};

const headerRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
};

const controlsRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24,
};

const rightControlsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
};

const viewToggleStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  border: '1px solid #d9d9d9',
  borderRadius: 6,
  padding: 2,
  background: '#fff',
};

const toggleButtonStyle: React.CSSProperties = {
  border: 'none',
  borderRadius: 4,
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  minWidth: 32,
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 600,
  color: '#1a1a1a',
  margin: 0,
};

const inviteCandidateButtonStyle: React.CSSProperties = {
  background: '#1d1f48',
  borderColor: '#1d1f48',
  borderRadius: 9999,
  color: '#fff',
  fontWeight: 500,
  padding: '0.5rem',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'center',
  textDecoration: 'none',
  minWidth: '200px',
};

const sortContainerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const tableStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
};

// Collapsible Filter Styles
const panelStyle: React.CSSProperties = {
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 0,
};

const checkboxGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  marginBottom: 16,
};

const checkboxItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
};

const checkboxStyle: React.CSSProperties = {
  fontSize: 14,
};

const clearFiltersStyle: React.CSSProperties = {
  color: '#1677ff',
  padding: 0,
  fontSize: 14,
  fontWeight: 500,
};

const noDataMessageStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#999',
  fontStyle: 'italic',
  padding: '12px 0',
};

export default ViewCandidates;